/**
 * What a widget knows about the tile it is in (`frontend.md` rule 7): its own
 * params and tab, and the tile's own commands. **A tile's state lives in the
 * tile**, in the layout store, so a fold, a swap or an expand — which move the
 * component without remounting it — lose nothing.
 */
import { createContext, useContext } from "react";

import type { Params, Slot } from "./layout";
import { useLayout } from "./useLayout";

export interface TileContext {
  id: number;
  /** Where the tile is now; it changes when a swap or an expand moves it. */
  index: Slot;
}

export const TileCtx = createContext<TileContext | null>(null);

export function useTileContext(): TileContext {
  const tile = useContext(TileCtx);
  if (!tile) throw new Error("a widget outside a tile");
  return tile;
}

/** The tile's params and tab, read from the store at the tile's current place. */
export function useTile() {
  const { id, index } = useTileContext();
  const tile = useLayout((s) => s.tiles[index]);
  const isWide = useLayout((s) => s.isWide);
  const expanded = useLayout((s) => s.expanded !== null);
  const store = useLayout.getState;
  const current = tile?.id === id ? tile : null;
  return {
    params: current?.params ?? null,
    tab: current?.tab ?? null,
    isWide,
    expanded,
    setTab: (tab: string) => store().patch(index, { tab }),
    setParams: (params: Params | null) => store().patch(index, { params }),
    close: () => store().close(index),
    expand: () => store().expand(index),
    collapse: () => store().collapse(),
  };
}

/** The tile's tab, or `fallback` until the person picks one. */
export function useTileTab(fallback: string): [string, (tab: string) => void] {
  const { tab, setTab } = useTile();
  return [tab ?? fallback, setTab];
}
